import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { SearchTable as SearchTableBase } from 'components/searchPlayerTable/SearchTable';
import { SearchTableRow } from 'components/searchPlayerTable/SearchTable/ui';

import { CompletedTransactionRow } from './CompletedTransactionRow';
import { CompletedTransactionsListHeader } from './CompletedTransactionsListHeader';
import type { CompletedTransactions_deal } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({
  gap: 3,
})`
  padding-bottom: var(--double-unit);
`;

const NegativeMargin = styled.div`
  margin: 0 calc(var(--unit) * -1);
`;

const SearchTable = styled(SearchTableBase)`
  --fixed-columns: 0;
  --stat-columns: 5;

  --player-cell-price: 0;
  --first-cell-width: 120px;
  --padding-first-cell-left: var(--unit);
  --padding-first-cell-right: var(--unit);
  --padding-last-cell-left: var(--unit);
  --padding-last-cell-right: var(--double-unit);

  grid-template-columns:
    var(--first-cell-width)
    repeat(calc(var(--stat-columns)), minmax(100px, 1fr));
  @media ${laptopAndAbove} {
    --first-cell-width: 200px;
    --padding-first-cell-left: var(--triple-unit);
    --padding-first-cell-right: var(--intermediate-unit);
    --padding-last-cell-left: var(--intermediate-unit);
    --padding-last-cell-right: var(--triple-unit);
  }
`;

const LastSearchTableRow = styled(SearchTableRow)`
  border: none;
`;

const AllColumn = styled.div`
  grid-column: 1/-1;
`;

const NB_COLUMNS = 5;

type Props = {
  transactions: CompletedTransactions_deal[];
  loading: boolean;
  hasMore: boolean;
  user: { slug: string };
  fetch: () => void;
};
export const CompletedTransactions = ({
  transactions,
  loading,
  hasMore,
  user,
  fetch,
}: Props) => {
  const { InfiniteScrollLoader } = useInfiniteScroll(fetch, hasMore, false);

  return (
    <Root>
      <HeadlineM>
        <FormattedMessage
          id="Player.CompletedTransactions.title"
          defaultMessage="Completed Transactions"
        />
      </HeadlineM>
      <NegativeMargin>
        <SearchTable
          nbStatsColumns={NB_COLUMNS}
          header={<CompletedTransactionsListHeader />}
        >
          <Skeleton loading={loading}>
            {transactions.map(transaction => (
              <CompletedTransactionRow
                key={transaction.id}
                deal={transaction}
                user={user}
              />
            ))}
          </Skeleton>
          <LastSearchTableRow>
            <AllColumn>
              <InfiniteScrollLoader small />
            </AllColumn>
          </LastSearchTableRow>
        </SearchTable>
      </NegativeMargin>
    </Root>
  );
};

CompletedTransactions.fragments = {
  deal: gql`
    fragment CompletedTransactions_deal on TokenOperationTradeInterface {
      id
      ...CompletedTransactionRow_deal
    }
    ${CompletedTransactionRow.fragments.deal}
  ` as TypedDocumentNode<CompletedTransactions_deal>,
};
